import axios from 'axios'
import businessSceneReference from '../../assets/商务.png'
import resortSceneReference from '../../assets/度假.png'
import japaneseSceneReference from '../../assets/日式.png'
import quickSceneReference from '../../assets/快捷.png'
import floorTowelReference from '../../assets/地巾参考图.png'

const TARGET_GENERATION_SIZE = '2730x1700'

// 豆包 Seedream API 配置
const API_CONFIG = {
  // 开发环境通过 Vite 代理规避浏览器跨域，生产环境建议走后端代理
  baseURL: import.meta.env.DEV ? '/api/ark' : 'https://ark.cn-beijing.volces.com/api/v3',
  apiKey: import.meta.env.VITE_ARK_API_KEY || import.meta.env.VITE_DOUBAO_API_KEY || '',
  model: import.meta.env.VITE_ARK_IMAGE_MODEL || 'doubao-seedream-5-0-260128',
  size: TARGET_GENERATION_SIZE,
  watermark: import.meta.env.VITE_ARK_WATERMARK === 'false' ? false : true,
  timeout: 180000,
}

const DEFAULT_IMAGE_SIZE = TARGET_GENERATION_SIZE
const PRODUCT_REFERENCE_PRESET = {
  maxDimension: 1400,
  maxApproxBytes: 320 * 1024,
}
const HOTEL_REFERENCE_PRESET = {
  maxDimension: 1600,
  maxApproxBytes: 420 * 1024,
}
const HOTEL_REFERENCE_ABSTRACTION = {
  detailMaxDimension: 220,
  outputMaxDimension: 1200,
  blurPx: 6,
}
const REFERENCE_ROLE_ORDER = [
  'bedding_fabric',
  'bedding_size',
  'bedding_pattern',
  'bathrobe_style',
  'bathrobe_material',
]

const apiClient = axios.create({
  baseURL: API_CONFIG.baseURL,
  timeout: API_CONFIG.timeout,
  headers: {
    'Content-Type': 'application/json',
  },
})

apiClient.interceptors.request.use(
  (config) => {
    if (!API_CONFIG.apiKey) {
      return Promise.reject(new Error('缺少豆包 API Key，请配置 VITE_ARK_API_KEY'))
    }

    config.headers.Authorization = `Bearer ${API_CONFIG.apiKey}`
    return config
  },
  (error) => Promise.reject(error)
)

apiClient.interceptors.response.use(
  (response) => response.data,
  (error) => {
    const errorMessage =
      error.response?.data?.error?.message ||
      error.response?.data?.message ||
      error.message ||
      '请求失败'

    console.error('API Error:', errorMessage)
    const enrichedError = new Error(errorMessage)
    enrichedError.status = error.response?.status
    enrichedError.code = error.response?.data?.error?.code || error.code
    enrichedError.responseData = error.response?.data
    return Promise.reject(enrichedError)
  }
)

const extractResultUrl = (response) => {
  if (typeof response?.url === 'string' && response.url) {
    return response.url
  }

  if (Array.isArray(response) && response[0]?.url) {
    return response[0].url
  }

  if (Array.isArray(response?.data) && response.data[0]?.url) {
    return response.data[0].url
  }

  if (response?.data?.[0]?.url) {
    return response.data[0].url
  }

  if (Array.isArray(response?.data) && response.data[0]?.image_url) {
    return response.data[0].image_url
  }

  if (response?.data?.[0]?.image_url) {
    return response.data[0].image_url
  }

  return null
}

const convertImageUrlToDataUrl = async (imageUrl) => {
  const response = await fetch(imageUrl)

  if (!response.ok) {
    throw new Error(`图片加载失败: ${response.status}`)
  }

  const blob = await response.blob()

  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => resolve(reader.result)
    reader.onerror = reject
    reader.readAsDataURL(blob)
  })
}

const estimateDataUrlBytes = (dataUrl) => {
  const base64 = dataUrl?.split(',')?.[1] || ''

  if (!base64) {
    return 0
  }

  const padding = base64.endsWith('==') ? 2 : base64.endsWith('=') ? 1 : 0
  return Math.floor((base64.length * 3) / 4) - padding
}

const loadImageElement = (src) =>
  new Promise((resolve, reject) => {
    const image = new Image()
    image.onload = () => resolve(image)
    image.onerror = () => reject(new Error('参考图解析失败'))
    image.src = src
  })

const compressReferenceImage = async (dataUrl, preset) => {
  const image = await loadImageElement(dataUrl)
  const originalMaxDimension = Math.max(image.naturalWidth, image.naturalHeight)
  const originalBytes = estimateDataUrlBytes(dataUrl)

  if (originalMaxDimension <= preset.maxDimension && originalBytes <= preset.maxApproxBytes) {
    return dataUrl
  }

  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')

  if (!ctx) {
    return dataUrl
  }

  let scale = Math.min(1, preset.maxDimension / originalMaxDimension)
  let attempt = 0
  let compressed = dataUrl

  while (attempt < 4) {
    const width = Math.max(1, Math.round(image.naturalWidth * scale))
    const height = Math.max(1, Math.round(image.naturalHeight * scale))

    canvas.width = width
    canvas.height = height
    ctx.clearRect(0, 0, width, height)
    ctx.drawImage(image, 0, 0, width, height)

    let quality = 0.88
    compressed = canvas.toDataURL('image/jpeg', quality)

    while (estimateDataUrlBytes(compressed) > preset.maxApproxBytes && quality > 0.68) {
      quality -= 0.06
      compressed = canvas.toDataURL('image/jpeg', quality)
    }

    if (estimateDataUrlBytes(compressed) <= preset.maxApproxBytes || scale <= 0.55) {
      break
    }

    scale *= 0.88
    attempt += 1
  }

  return compressed
}

const abstractHotelReferenceImage = async (dataUrl) => {
  const image = await loadImageElement(dataUrl)
  const sourceMaxDimension = Math.max(image.naturalWidth, image.naturalHeight)
  const detailScale = Math.min(1, HOTEL_REFERENCE_ABSTRACTION.detailMaxDimension / sourceMaxDimension)
  const detailWidth = Math.max(1, Math.round(image.naturalWidth * detailScale))
  const detailHeight = Math.max(1, Math.round(image.naturalHeight * detailScale))

  const detailCanvas = document.createElement('canvas')
  const detailCtx = detailCanvas.getContext('2d')

  if (!detailCtx) {
    return dataUrl
  }

  detailCanvas.width = detailWidth
  detailCanvas.height = detailHeight
  detailCtx.imageSmoothingEnabled = true
  detailCtx.drawImage(image, 0, 0, detailWidth, detailHeight)

  const outputScale = Math.min(1, HOTEL_REFERENCE_ABSTRACTION.outputMaxDimension / sourceMaxDimension)
  const outputWidth = Math.max(1, Math.round(image.naturalWidth * outputScale))
  const outputHeight = Math.max(1, Math.round(image.naturalHeight * outputScale))

  const outputCanvas = document.createElement('canvas')
  const outputCtx = outputCanvas.getContext('2d')

  if (!outputCtx) {
    return dataUrl
  }

  outputCanvas.width = outputWidth
  outputCanvas.height = outputHeight
  outputCtx.imageSmoothingEnabled = true
  outputCtx.filter = `blur(${HOTEL_REFERENCE_ABSTRACTION.blurPx}px) saturate(0.85) contrast(0.9) brightness(1.02)`
  outputCtx.drawImage(detailCanvas, 0, 0, outputWidth, outputHeight)
  outputCtx.filter = 'none'
  outputCtx.fillStyle = 'rgba(255,255,255,0.08)'
  outputCtx.fillRect(0, 0, outputWidth, outputHeight)

  return outputCanvas.toDataURL('image/jpeg', 0.82)
}

const normalizeReferenceImage = async (reference, preset) => ({
  ...reference,
  base64: await compressReferenceImage(reference.base64, preset),
})

const formatReferenceList = (references) =>
  references.map((reference) => `参考图${reference.index}`).join('、')

const getOrderedProductReferences = (referenceImages = []) =>
  REFERENCE_ROLE_ORDER.map((role) => referenceImages.find((reference) => reference.role === role)).filter(
    (reference) => reference?.base64
  )

const decorateReferenceIndexes = (references = []) =>
  references.map((reference, index) => ({
    ...reference,
    index: index + 1,
  }))

const joinPromptSections = (sections = []) => sections.filter(Boolean).join('\n')

const getReferencePromptOrder = (referenceOrReferences) => {
  const references = Array.isArray(referenceOrReferences)
    ? referenceOrReferences.filter(Boolean)
    : [referenceOrReferences].filter(Boolean)

  return references[0]?.index ?? Number.MAX_SAFE_INTEGER
}

const buildOrderedReferencePromptSections = (items = []) =>
  items
    .filter((item) => item?.text)
    .sort((left, right) => getReferencePromptOrder(left.references) - getReferencePromptOrder(right.references))
    .map((item) => item.text)

const resolveRequestSize = (size) => {
  if (/^\d+x\d+$/.test(size)) {
    return size
  }

  console.warn(`无效的图片尺寸配置 "${size}"，已回退到 ${DEFAULT_IMAGE_SIZE}`)
  return DEFAULT_IMAGE_SIZE
}

const validateRequestConfig = ({ model, size, referenceCount }) => {
  if (!model) {
    throw new Error('缺少图片生成模型配置')
  }

  if (!/seedream/i.test(model) && referenceCount > 1) {
    throw new Error(`当前模型 ${model} 未启用多参考图校验，请使用 Seedream 系列模型`)
  }

  if (!/^\d+x\d+$/.test(size)) {
    throw new Error(`图片尺寸配置无效: ${size}`)
  }
}

const HOTEL_REFERENCE_BY_STYLE = {
  quick: {
    preview: quickSceneReference,
    labelZh: '快捷酒店空间构图参考',
    labelEn: 'Quick Hotel Composition Reference',
  },
  business: {
    preview: businessSceneReference,
    labelZh: '商务酒店空间构图参考',
    labelEn: 'Business Hotel Composition Reference',
  },
  japanese: {
    preview: japaneseSceneReference,
    labelZh: '日式酒店空间构图参考',
    labelEn: 'Japanese Hotel Composition Reference',
  },
  resort: {
    preview: resortSceneReference,
    labelZh: '度假酒店空间构图参考',
    labelEn: 'Resort Hotel Composition Reference',
  },
}

const hotelReferenceCache = new Map()

const getHotelReferenceImages = async (styleId) => {
  const sceneReference = HOTEL_REFERENCE_BY_STYLE[styleId]

  if (!sceneReference) {
    return []
  }

  if (!hotelReferenceCache.has(styleId)) {
    const referencePromise = convertImageUrlToDataUrl(sceneReference.preview)
      .then((dataUrl) => abstractHotelReferenceImage(dataUrl))
      .then((dataUrl) =>
        normalizeReferenceImage(
          {
            id: `hotel-room-reference-${styleId}`,
            role: 'hotel_room_wide_1',
            labelZh: sceneReference.labelZh,
            labelEn: sceneReference.labelEn,
            preview: sceneReference.preview,
            base64: dataUrl,
          },
          HOTEL_REFERENCE_PRESET
        )
      )
      .then((reference) => [reference])
      .catch((error) => {
        console.warn(`酒店参考图预处理失败(${styleId})，已退化为仅使用商品参考图。`, error)
        hotelReferenceCache.delete(styleId)
        return []
      })

    hotelReferenceCache.set(styleId, referencePromise)
  }

  return hotelReferenceCache.get(styleId)
}

let bedsideAccessoryReferencePromise = null

const getBedsideAccessoryReferenceImages = async ({ style, sourceMeta }) => {
  const isBundleSelection = sourceMeta?.productType === 'bundle'

  if (!style?.addsBedsideItems || !isBundleSelection) {
    return []
  }

  if (!bedsideAccessoryReferencePromise) {
    bedsideAccessoryReferencePromise = convertImageUrlToDataUrl(floorTowelReference)
      .then((dataUrl) =>
        normalizeReferenceImage(
          {
            id: 'floor-towel-reference',
            role: 'floor_towel_style',
            labelZh: '地巾摆放参考',
            labelEn: 'Floor Towel Placement Reference',
            preview: floorTowelReference,
            base64: dataUrl,
          },
          PRODUCT_REFERENCE_PRESET
        )
      )
      .then((reference) => [reference])
      .catch((error) => {
        console.warn('地巾参考图预处理失败，已退化为仅使用商品参考图和客房参考图。', error)
        bedsideAccessoryReferencePromise = null
        return []
      })
  }

  return bedsideAccessoryReferencePromise
}

const buildPromptText = ({ style, sourceMeta, references = [], requestId }) => {
  const styleName = style.nameZh || style.name
  const styleDesc = style.descriptionZh || style.description || ''
  const stylePrompt = style.promptZh || style.promptEn || ''
  const isBundleSelection = sourceMeta?.productType === 'bundle'
  const bedding = sourceMeta?.bedding
  const bathrobe = sourceMeta?.bathrobe
  const isStripe = bedding?.fabricId?.startsWith('stripe')
  const isWindowGrid = bedding?.fabricId === 'window-grid'
  const bathrobeRackPrompt = bathrobe
    ? '浴袍必须只出现一件，并且完整挂在独立落地浴袍架上。'
    : ''
  const windowGridScenePrompt = isWindowGrid
    ? `房间场景只通过文字描述体现${styleName}风格，重点调整墙面、灯光、家具、窗帘与软装氛围，让空间符合${styleName}酒店气质，但不要改动床上的四件套。`
    : ''
  const stripeOrientationPrompt =
    isStripe
      ? '所有条纹四件套都必须生成成纵向条纹效果，条纹方向沿床头到床尾纵向延伸，在床的两侧也是由床头纵向延伸到床位，不能横向铺陈，也不能在被面或两侧床面或枕套上旋转成不同方向。'
      : ''
  const variationPrompt =
    requestId != null
      ? `本次输出新的场景变体（编号${requestId}），在保持床完整入镜、产品不变的前提下，房间软装和搭配细节要与之前结果明显不同。`
      : ''
  const beddingSpecPrompt = bedding?.fabricSpecHintZh || bedding?.fabricSpecHintEn || ''
  const compositionPrompt =
    '镜头按酒店参考图拍摄，保持相同机位和视角；必须是完整客房中远景，整张床完整入镜，床尾前方保留地面，不能裁掉床头、床尾或床侧。'
  const hotelRoomPrompt =
    `房间里的挂画、灯具、窗帘、家具、地毯和摆件不要照抄参考图，也不要删除，可以替换成符合${styleName}风格的其他款式。`
  const distancePrompt =
    ''
  const structurePrompt =
    ''
  const internalReferencePrompt =
    ''
  const compositionStandardPrompt =
    style.id === 'japanese'
      ? '日式场景中床可略偏左，但仍需完整入镜，右侧保留障子门，床尾前方保留榻榻米地面。'
      : ''
  const humanExclusionPrompt =
    '画面中不要出现人物、脚、脚趾、腿或任何人体部位；拖鞋如果出现，必须是空拖鞋。'
  const accessoriesPrompt =
    style.addsBedsideItems && isBundleSelection
      ? '床侧前景只放一双空拖鞋和一条单层纯白地巾。拖鞋内外都不能出现脚趾、脚、腿、人体皮肤或穿着拖鞋的人。地巾必须放在床侧地面上，单层平铺，四边清晰，不要叠放、不要悬空、不要铺在床上。'
      : ''
  const indexedReferences = decorateReferenceIndexes(references)
  const getReferenceByRole = (role) => indexedReferences.find((reference) => reference.role === role)
  const hotelReferences = indexedReferences.filter((reference) => reference.role?.startsWith('hotel_room_wide_'))

  if (isBundleSelection) {
    const fabricName = bedding?.fabricNameZh || bedding?.fabricNameEn || '面料'
    const variantName =
      bedding?.variantPromptNameZh || bedding?.variantNameZh || bedding?.variantPromptNameEn || bedding?.variantNameEn || '花型方案'
    const beddingFabricReference = getReferenceByRole('bedding_fabric')
    const beddingSizeReference = getReferenceByRole('bedding_size')
    const beddingVariantReference = getReferenceByRole('bedding_pattern')
    const bathrobeStyleReference = getReferenceByRole('bathrobe_style')
    const bathrobeMaterialReference = getReferenceByRole('bathrobe_material')
    const floorTowelStyleReference = getReferenceByRole('floor_towel_style')
    const stripeColorPrompt = isStripe
      ? `四件套条纹颜色必须为${variantName}，保持同色系的明暗层次，不能偏成其他颜色。`
      : ''

    if (isWindowGrid) {
      const orderedReferencePromptSections = buildOrderedReferencePromptSections([
        {
          references: beddingSizeReference,
          text: beddingSizeReference
            ? `根据参考图${beddingSizeReference.index}生成床上四件套，颜色为${variantName}。`
            : '',
        },
        {
          references: bathrobeStyleReference,
          text: bathrobeStyleReference
            ? `参考图${bathrobeStyleReference.index}约束浴袍款式，只保留轮廓、版型、领型和长度。`
            : '',
        },
        {
          references: bathrobeMaterialReference,
          text: bathrobeMaterialReference
            ? `参考图${bathrobeMaterialReference.index}约束浴袍面料触感和纹理，四件套回字格不能出现在浴袍上。`
            : '',
        },
        {
          references: floorTowelStyleReference,
          text: floorTowelStyleReference
            ? `参考图${floorTowelStyleReference.index}。`
            : '',
        },
      ])

      return joinPromptSections([
        '任务：生成一张高端酒店客房布草效果图。',
        ...orderedReferencePromptSections,
        '床上的四件套保持参考图效果不变，只变换酒店房间场景。',
        windowGridScenePrompt,
        bathrobeRackPrompt,
        humanExclusionPrompt,
        variationPrompt,
        compositionStandardPrompt,
        `场景风格：${styleName}。${styleDesc}${stylePrompt}`,
        accessoriesPrompt,
      ])
    }

    return joinPromptSections([
      '请综合提供的产品参考图和酒店客房参考图，生成一张高端酒店客房布草效果图。',
      beddingFabricReference
        ? `参考图${beddingFabricReference.index}是四件套面料图，必须准确保留 ${fabricName} 的面料肌理、织法质感和细节表现。`
        : '',
      beddingVariantReference
        ? isStripe
          ? `参考图${beddingVariantReference.index}是四件套条纹参考图，必须准确保留条纹宽度、疏密、明暗层次和图案布局。`
          : `参考图${beddingVariantReference.index}是四件套印花或颜色图，必须准确保留 ${variantName} 的花型、颜色、图案布局和配色关系。`
        : '',
      stripeOrientationPrompt,
      stripeColorPrompt,
      beddingSpecPrompt ? `四件套规格要求：${beddingSpecPrompt}` : '如果参考面料存在明确的条纹宽度、格纹尺寸、印花比例或纹理尺度，必须按真实规格呈现。',
      bathrobeStyleReference
        ? `参考图${bathrobeStyleReference.index}是浴袍款式图，必须保留对应浴袍成品的轮廓、版型、领型、长度和酒店陈列效果。`
        : '',
      bathrobeMaterialReference
        ? `参考图${bathrobeMaterialReference.index}是浴袍面料图，必须保留浴袍面料的触感、厚薄和表面纹理。四件套的印花或者颜色图坚决不能出现在浴袍面料上。`
        : '',
      floorTowelStyleReference
        ? `参考图${floorTowelStyleReference.index}是床侧地巾参考图，用于约束地巾的纯白颜色和地面单层平铺状态；地巾必须放在床侧地面上，四边清晰，不能叠放、悬空或铺到床上。`
        : '',
      hotelReferences.length
        ? `${formatReferenceList(hotelReferences)}只用于约束镜头视角、床与房间的空间关系以及整体风格，不用于复制房间里的具体物件款式。`
        : '',
      '',
      '四件套参考图是硬约束，优先级高于房间软装。',
      bathrobeRackPrompt,
      humanExclusionPrompt,
      '浴袍不能抢占床的主体位置。',
      compositionPrompt,
      hotelRoomPrompt,
      distancePrompt,
      structurePrompt,
      internalReferencePrompt,
      variationPrompt,
      compositionStandardPrompt,
      `房间整体场景采用${styleName}风格。${styleDesc}${stylePrompt}`,
      accessoriesPrompt,
    ])
  }

  const sourceSubject = sourceMeta?.optionNameZh || sourceMeta?.optionNameEn || '浴袍'
  return joinPromptSections([
    `请综合所有参考图，生成 ${styleName} 场景下的 ${sourceSubject} 展示图。`,
    `主体必须保持为 ${sourceSubject}，准确保留款式、颜色和材质特征，适合酒店布草项目展示。`,
    `${styleDesc}${stylePrompt}`,
    accessoriesPrompt,
  ])
}

const startProgressSimulation = (onProgress) => {
  if (!onProgress) {
    return () => {}
  }

  let progress = 12
  onProgress(progress)

  const timer = window.setInterval(() => {
    progress = Math.min(progress + Math.random() * 9, 88)
    onProgress(Math.floor(progress))
  }, 700)

  return () => window.clearInterval(timer)
}

const getGenerationTimestamp = () => {
  if (typeof performance !== 'undefined' && typeof performance.now === 'function') {
    return performance.now()
  }

  return Date.now()
}

const formatGenerationDuration = (milliseconds) => {
  if (milliseconds < 1000) {
    return `${Math.round(milliseconds)}ms`
  }

  const totalSeconds = milliseconds / 1000

  if (totalSeconds < 60) {
    return `${totalSeconds.toFixed(2)}s`
  }

  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds - minutes * 60

  return `${minutes}m ${seconds.toFixed(2)}s`
}

/**
 * 生成场景图 - 豆包 Seedream 主入口
 * @param {Object} params
 * @param {string} params.sourceImage - 源图片（Base64）
 * @param {Object} params.style - 场景对象
 * @param {Object} params.sourceMeta - 选择的商品元数据
 * @param {Function} onProgress - 进度回调
 * @returns {Promise<{resultUrl: string}>}
 */
export const generateStylizedImage = async ({ sourceImage, style, sourceMeta, requestId }, onProgress) => {
  const generationStartedAt = getGenerationTimestamp()
  const generationStartedDate = new Date()
  let generationStatus = '成功'
  let stopProgress = () => {}

  console.log('=== 图片生成开始 ===')
  console.log('请求ID:', requestId ?? '未提供')
  console.log('开始时间:', generationStartedDate.toLocaleString())

  try {
    if (!sourceImage) {
      throw new Error('缺少源图片数据')
    }

    if (!style) {
      throw new Error('缺少场景信息')
    }

    stopProgress = startProgressSimulation(onProgress)

    const styleName = style.nameZh || style.name
    const isWindowGridSelection = sourceMeta?.bedding?.fabricId === 'window-grid'
    const orderedProductReferences = getOrderedProductReferences(sourceMeta?.referenceImages)
    const normalizedProductReferences = await Promise.all(
      orderedProductReferences.map((reference) =>
        normalizeReferenceImage(reference, PRODUCT_REFERENCE_PRESET)
      )
    )
    const bedsideAccessoryReferenceImages = await getBedsideAccessoryReferenceImages({ style, sourceMeta })
    const hotelReferenceImages = isWindowGridSelection ? [] : await getHotelReferenceImages(style.id)
    const referenceImages = [...normalizedProductReferences, ...bedsideAccessoryReferenceImages, ...hotelReferenceImages].slice(
      0,
      REFERENCE_ROLE_ORDER.length + 2
    )
    const imagePayload = referenceImages.length
      ? referenceImages.map((reference) => reference.base64)
      : sourceImage
    const requestSize = resolveRequestSize(API_CONFIG.size)

    validateRequestConfig({
      model: API_CONFIG.model,
      size: requestSize,
      referenceCount: Array.isArray(imagePayload) ? imagePayload.length : 1,
    })

    const promptText = buildPromptText({
      style,
      sourceMeta,
      references: referenceImages,
      requestId,
    })
    const requestBody = {
      model: API_CONFIG.model,
      prompt: promptText,
      image: imagePayload,
      sequential_image_generation: 'disabled',
      response_format: 'url',
      size: requestSize,
      stream: false,
      watermark: API_CONFIG.watermark,
    }

    console.log('=== 豆包 API 请求参数 ===')
    console.log('模型:', API_CONFIG.model)
    console.log('场景:', styleName)
    console.log('来源元信息:', sourceMeta)
    console.log('参考图数量:', Array.isArray(imagePayload) ? imagePayload.length : 1)
    console.log(
      '参考图角色:',
      referenceImages.map((reference) => reference.role)
    )
    console.log('请求体:', {
      ...requestBody,
      image: Array.isArray(imagePayload)
        ? imagePayload.map((_, index) => `[BASE64_IMAGE_DATA_${index + 1}]`)
        : '[BASE64_IMAGE_DATA]',
    })

    const response = await apiClient.post('/images/generations', requestBody)
    const resultUrl = extractResultUrl(response)

    if (!resultUrl) {
      throw new Error(`豆包接口未返回图片 URL: ${JSON.stringify(response)}`)
    }

    if (onProgress) {
      onProgress(100)
    }

    return { resultUrl }
  } catch (error) {
    generationStatus = '失败'
    console.error('豆包生成图片失败:', {
      message: error.message,
      status: error.status,
      code: error.code,
      details: error.responseData,
    })
    throw error
  } finally {
    stopProgress?.()

    const generationEndedAt = getGenerationTimestamp()
    const generationEndedDate = new Date()
    const duration = generationEndedAt - generationStartedAt

    console.log('=== 图片生成结束 ===')
    console.log('请求ID:', requestId ?? '未提供')
    console.log('结束状态:', generationStatus)
    console.log('结束时间:', generationEndedDate.toLocaleString())
    console.log('总耗时:', formatGenerationDuration(duration))
  }
}

export const mockGenerateImage = async (onProgress) => {
  return new Promise((resolve) => {
    let progress = 0
    const interval = setInterval(() => {
      progress += Math.random() * 15
      if (progress > 100) progress = 100
      onProgress?.(Math.floor(progress))

      if (progress >= 100) {
        clearInterval(interval)
        resolve({
          resultUrl: 'https://picsum.photos/1024/1024?random=' + Date.now(),
        })
      }
    }, 500)
  })
}

export default apiClient
